// Custom tender types (legacy `tender_types`; backoffice Settings > Order & Checkout > POS Functions > Tender Types;
// register Other Payment Options, W-44): alternative tenders outside cash and card, such as a third-party delivery
// payment or a paper gift certificate. One with extra details asks the register for a value titled `detailTitle`
// ("Enter Voucher Number below") and keeps it on the payment. Location config, written at the node or in the cloud.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { LocationId, TenderTypeId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { CatalogEntityNotFound, CatalogInvalid } from './errors.ts';

export const TenderTypeFields = z
  .object({
    name: z.string().trim().min(1).max(255),
    allowExtraDetails: z.boolean(),
    /** "Voucher Number": the title of the value the register asks for; null unless extra details are allowed. */
    detailTitle: z.string().trim().min(1).max(255).nullable(),
  })
  .meta({ id: 'TenderTypeFields' });
export const TenderTypeView = TenderTypeFields.extend({ tenderTypeId: TenderTypeId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'TenderTypeView' });

const common = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound], dryRun: true,
} as const;

export const UpsertTenderType = defineCommand({
  ...common,
  name: 'catalog.upsertTenderType',
  summary: 'Create or change a custom tender type (an alternative tender such as a third-party payment or a paper certificate).',
  input: z.object({ tenderTypeId: TenderTypeId, locationId: LocationId, changes: TenderTypeFields.partial() }),
  output: TenderTypeView,
  permission: 'settings.order_checkout.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/tender-types', 'PUT /v1/tender-types/{id}'],
});

export const DeleteTenderType = defineCommand({
  ...common,
  name: 'catalog.deleteTenderType',
  summary: 'Delete a custom tender type. Payments already taken with it keep its name.',
  input: z.object({ tenderTypeId: TenderTypeId, locationId: LocationId }),
  output: z.object({ id: TenderTypeId, deleted: z.literal(true) }),
  permission: 'settings.order_checkout.manage',
  risk: 'medium',
  agent: { expose: false },
  legacy: ['DELETE /v1/tender-types/{id}'],
});

export const ListTenderTypes = defineQuery({
  name: 'catalog.tenderTypes.list',
  summary: "The location's custom tender types.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ tenderTypes: z.array(TenderTypeView) }),
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  // Legacy's GET /v1/tender-types sits behind manage_order_checkout_settings, as its page does (lane P2, item 6).
  permission: 'settings.order_checkout.manage',
  legacy: ['GET /v1/tender-types'],
});

export const tenderTypeCommands = [UpsertTenderType, DeleteTenderType] as const;
export const tenderTypeQueries = [ListTenderTypes] as const;
